import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet'; // Import Helmet for SEO
import styles from '../styles/MothersDay.module.css';
import CTAButton from '../components/MothersDay/CTAButton';

const MothersDay = () => {
  const [timeRemaining, setTimeRemaining] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const [isMotherDaySamplePlaying, setIsMotherDaySamplePlaying] = useState(false);
  const [showOrderNotification, setShowOrderNotification] = useState(false);
  const [currentNotificationIndex, setCurrentNotificationIndex] = useState(0);
  const [currentGenreGroup, setCurrentGenreGroup] = useState(0);
  const [playingGenre, setPlayingGenre] = useState(null);
  const [showStickyCta, setShowStickyCta] = useState(true);
  const [isSwiping, setIsSwiping] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);

  const audioRef = useRef(null);
  const motherDaySampleAudioRef = useRef(null);
  const genreAudioRef = useRef(null);
  const containerRef = useRef(null);

  const notifications = [
    { text: "Someone just ordered a custom song! 2 mins ago", time: "2 mins ago" },
    { text: "Someone just ordered a custom song! 5 mins ago", time: "5 mins ago" },
    { text: "Someone just ordered a custom song! 10 mins ago", time: "10 mins ago" },
  ];

  const musicSamples = [
    {
      id: 'sample1',
      title: "Mom's Lullaby",
      genre: "Folk",
      description: "Gentle acoustic guitar with heartfelt vocals",
      audioPath: "/audio/folk-sample.mp3",
    },
    {
      id: 'sample2',
      title: "Memories of You",
      genre: "Pop",
      description: "Upbeat modern pop with piano and strings",
      audioPath: "/audio/pop-sample.mp3",
    },
    {
      id: 'sample3',
      title: "Thank You, Mom",
      genre: "Country",
      description: "Warm country melody with emotional lyrics",
      audioPath: "/audio/country-sample.mp3",
    },
  ];

  const genres = [
    { name: 'Country', color: '#F59E0B', icon: '🎸', description: 'Heartfelt lyrics with acoustic guitars, fiddles, and warm vocal storytelling.' },
    { name: 'Classic Rock', color: '#EF4444', icon: '🤘', description: 'Powerful electric guitars, drums, and iconic vocals reminiscent of the 60s-80s.' },
    { name: 'Pop', color: '#EC4899', icon: '💃', description: 'Catchy melodies, contemporary sounds, and upbeat rhythms.' },
    { name: 'R&B', color: '#8B5CF6', icon: '🎤', description: 'Smooth vocals, soulful instrumentals, and emotional depth.' },
    { name: 'Folk', color: '#10B981', icon: '🌿', description: 'Storytelling through acoustic instruments and authentic vocals.' },
    { name: 'Electronic', color: '#3B82F6', icon: '⚡️', description: 'Modern beats, synthesizers, and innovative sound design.' },
    { name: 'Jazz', color: '#F97316', icon: '🎷', description: 'Improvisation, syncopated rhythms, and rich harmonies with expressive solos.' },
    { name: 'Hip-Hop', color: '#6366F1', icon: '🎧', description: 'Rhythmic vocals, beats, and samples with emphasis on wordplay and flow.' },
    { name: 'Rap', color: '#14B8A6', icon: '🔥', description: 'Spoken word delivery over beats with focus on lyrical storytelling and rhythm.' },
  ];

  const genreGroups = [
    [genres[0], genres[1], genres[2], genres[3]],
    [genres[4], genres[5], genres[6], genres[7]],
    [genres[8]],
  ];

  const genreSamplePaths = {
    Country: '/audio/country-sample.mp3',
    'Classic Rock': '/audio/classic-rock-sample.mp3',
    Pop: '/audio/pop-sample.mp3',
    'R&B': '/audio/rnb-sample.mp3',
    Folk: '/audio/folk-sample.mp3',
    Electronic: '/audio/electronic-sample.mp3',
    Jazz: '/audio/jazz-sample.mp3',
    'Hip-Hop': '/audio/hip-hop-sample.mp3',
    Rap: '/audio/rap-sample.mp3',
  };

  useEffect(() => {
    const calculateMothersDay = () => {
      const now = new Date();
      const year = now.getFullYear();
      let mothersDay = new Date(year, 4, 1);
      mothersDay.setDate(mothersDay.getDate() + (7 - mothersDay.getDay()) % 7 + 7);
      mothersDay.setHours(23, 59, 59, 999);
      return mothersDay;
    };

    const updateTimer = () => {
      const now = new Date();
      const difference = calculateMothersDay() - now;
      if (difference <= 0) {
        setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      setTimeRemaining({
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000),
      });
    };

    updateTimer();
    const timer = setInterval(updateTimer, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const showNotification = () => {
      setShowOrderNotification(true);
      setCurrentNotificationIndex(Math.floor(Math.random() * notifications.length));
      setTimeout(() => setShowOrderNotification(false), 5000);
    };
    const initialTimer = setTimeout(showNotification, 10000);
    const intervalTimer = setInterval(showNotification, 30000);
    return () => {
      clearTimeout(initialTimer);
      clearInterval(intervalTimer);
    };
  }, []);

  useEffect(() => {
    if (containerRef.current) {
      const translateValue = -currentGenreGroup * (100 / genreGroups.length);
      containerRef.current.style.transform = `translateX(${translateValue}%)`;
    }
  }, [currentGenreGroup]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isMouseDown) return;
      e.preventDefault();
      setCurrentX(e.clientX);
      if (containerRef.current && startX !== 0) {
        const currentTranslate = -currentGenreGroup * (100 / genreGroups.length);
        const containerWidth = containerRef.current.offsetWidth;
        const diff = ((currentX - startX) / containerWidth) * 100;
        const cappedDiff = Math.min(Math.max(diff, -20), 20);
        containerRef.current.style.transform = `translateX(${currentTranslate + cappedDiff * 0.5}%)`;
      }
    };

    const handleMouseUp = () => {
      if (!isMouseDown) return;
      if (startX !== 0 && currentX !== 0) {
        const distance = startX - currentX;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;
        if (isLeftSwipe) {
          nextGenreGroup();
        } else if (isRightSwipe) {
          prevGenreGroup();
        } else {
          const translateValue = -currentGenreGroup * (100 / genreGroups.length);
          if (containerRef.current) {
            containerRef.current.style.transform = `translateX(${translateValue}%)`;
          }
        }
      }
      setIsMouseDown(false);
      setStartX(0);
      setCurrentX(0);
      setIsSwiping(false);
      if (containerRef.current) {
        containerRef.current.style.cursor = 'grab';
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isMouseDown, startX, currentX, currentGenreGroup]);

  const handlePlayPause = (sampleId) => {
    if (genreAudioRef.current) {
      genreAudioRef.current.pause();
      genreAudioRef.current.currentTime = 0;
      setPlayingGenre(null);
    }
    if (motherDaySampleAudioRef.current) {
      motherDaySampleAudioRef.current.pause();
      motherDaySampleAudioRef.current.currentTime = 0;
      setIsMotherDaySamplePlaying(false);
    }
    if (currentlyPlaying === sampleId) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setCurrentlyPlaying(null);
      return;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    const sample = musicSamples.find((s) => s.id === sampleId);
    if (sample) {
      audioRef.current = new Audio(sample.audioPath);
      audioRef.current.play().catch((error) => console.error('Audio playback failed:', error));
      setCurrentlyPlaying(sampleId);
      audioRef.current.onended = () => setCurrentlyPlaying(null);
    }
  };

  const handleMotherDaySamplePlay = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setCurrentlyPlaying(null);
    }
    if (genreAudioRef.current) {
      genreAudioRef.current.pause();
      genreAudioRef.current.currentTime = 0;
      setPlayingGenre(null);
    }
    if (isMotherDaySamplePlaying) {
      motherDaySampleAudioRef.current.pause();
      motherDaySampleAudioRef.current.currentTime = 0;
      setIsMotherDaySamplePlaying(false);
      return;
    }
    if (motherDaySampleAudioRef.current) {
      motherDaySampleAudioRef.current.pause();
      motherDaySampleAudioRef.current.currentTime = 0;
    }
    motherDaySampleAudioRef.current = new Audio('/audio/mothers-day-sample.mp3');
    motherDaySampleAudioRef.current.play().catch((error) => console.error('Audio playback failed:', error));
    setIsMotherDaySamplePlaying(true);
    motherDaySampleAudioRef.current.onended = () => setIsMotherDaySamplePlaying(false);
  };

  const handleGenrePlay = (genre, e) => {
    e.stopPropagation();
    if (isSwiping) return;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setCurrentlyPlaying(null);
    }
    if (motherDaySampleAudioRef.current) {
      motherDaySampleAudioRef.current.pause();
      motherDaySampleAudioRef.current.currentTime = 0;
      setIsMotherDaySamplePlaying(false);
    }
    if (playingGenre === genre.name) {
      genreAudioRef.current.pause();
      genreAudioRef.current.currentTime = 0;
      setPlayingGenre(null);
      return;
    }
    if (genreAudioRef.current) {
      genreAudioRef.current.pause();
      genreAudioRef.current.currentTime = 0;
    }
    genreAudioRef.current = new Audio(genreSamplePaths[genre.name]);
    genreAudioRef.current.play().catch((error) => console.error('Audio playback failed:', error));
    setPlayingGenre(genre.name);
    genreAudioRef.current.onended = () => setPlayingGenre(null);
  };

  const prevGenreGroup = () => {
    setCurrentGenreGroup((prev) => (prev === 0 ? genreGroups.length - 1 : prev - 1));
    setPlayingGenre(null);
    if (genreAudioRef.current) {
      genreAudioRef.current.pause();
      genreAudioRef.current.currentTime = 0;
    }
  };

  const nextGenreGroup = () => {
    setCurrentGenreGroup((prev) => (prev === genreGroups.length - 1 ? 0 : prev + 1));
    setPlayingGenre(null);
    if (genreAudioRef.current) {
      genreAudioRef.current.pause();
      genreAudioRef.current.currentTime = 0;
    }
  };

  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
    setTouchEnd(null);
    setIsSwiping(false);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.touches[0].clientX);
    if (touchStart && containerRef.current) {
      const diff = touchStart - e.touches[0].clientX;
      if (Math.abs(diff) > 20) {
        setIsSwiping(true);
      }
      const currentTranslate = -currentGenreGroup * (100 / genreGroups.length);
      const containerWidth = containerRef.current.offsetWidth;
      const cappedDiff = Math.min(Math.max(((e.touches[0].clientX - touchStart) / containerWidth) * 100, -20), 20);
      containerRef.current.style.transform = `translateX(${currentTranslate + cappedDiff * 0.5}%)`;
    }
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) {
      setIsSwiping(false);
      return;
    }
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    if (isLeftSwipe) {
      nextGenreGroup();
    } else if (isRightSwipe) {
      prevGenreGroup();
    } else {
      const translateValue = -currentGenreGroup * (100 / genreGroups.length);
      if (containerRef.current) {
        containerRef.current.style.transform = `translateX(${translateValue}%)`;
      }
    }
    setTouchStart(null);
    setTouchEnd(null);
    setIsSwiping(false);
  };

  const handleMouseDown = (e) => {
    setIsMouseDown(true);
    setStartX(e.clientX);
    setCurrentX(e.clientX);
    setIsSwiping(true);
    if (containerRef.current) {
      containerRef.current.style.cursor = 'grabbing';
    }
    e.preventDefault();
  };

  const formatNumber = (num) => (num < 10 ? `0${num}` : num);

  return (
    <div className={styles.mothersDayContainer}>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Mother's Day Custom Songs - 50% Off | MakeMeSongs</title>
        <meta
          name="description"
          content="Celebrate Mom with a personalized song! Use code MOM2025 for 50% off a custom song. The perfect Mother's Day gift to capture her unique story."
        />
        <meta
          name="keywords"
          content="Mother's Day custom songs, personalized Mother's Day gift, custom song for mom, MOM2025, Mother's Day music gift"
        />
        <meta property="og:title" content="Mother's Day Custom Songs - 50% Off | MakeMeSongs" />
        <meta
          property="og:description"
          content="Create a heartfelt custom song for Mom this Mother's Day. Use code MOM2025 for 50% off. A unique gift she'll cherish forever."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://makemesongs.com/mothers-day" />
        <meta property="og:image" content="https://makemesongs.com/images/mothers-day-gift.jpg" />
        <link rel="canonical" href="https://makemesongs.com/mothers-day" />
      </Helmet>

      <div className={styles.heroSection}>
        <div className={styles.contentContainer}>
          <div className={styles.specialOfferBanner}>
            <span className={styles.offerLabel}>LIMITED TIME OFFER</span> - Give Mom the gift of a Lifetime!
          </div>
          <div className={styles.mainContent}>
            <div className={styles.contentColumn}>
              <h1 className={styles.mainTitle}>Mother's Day Special</h1>
              <h2 className={styles.subTitle}>50% OFF Custom Songs</h2>
              <p className={styles.descriptionText}>
              Give your mom a gift that speaks to her soul - a custom song that's uniquely hers. MakeMeSongs will transform your special memories into a musical creation that will move her to tears and create a keepsake she'll treasure forever.
              </p>
              <div className={styles.highlightBox}>
                <h3 className={styles.highlightTitle}>Why a Custom Song for Mother's Day?</h3>
                <ul className={styles.benefitsList}>
                  <li>Expresses your love in a way words alone cannot</li>
                  <li>Creates a timeless memory she can replay forever</li>
                  <li>Tells her unique story through beautiful music</li>
                  <li>Shows thoughtfulness beyond traditional gifts</li>
                  <li>Perfect for sharing at family gatherings</li>
                </ul>
              </div>
              <div className={styles.ctaButtons}>
                <CTAButton className={styles.primaryButton}>Get Started Now</CTAButton>
                <button
                  onClick={handleMotherDaySamplePlay}
                  className={`${styles.playButton} ${isMotherDaySamplePlaying ? styles.playing : ''}`}
                  aria-label={isMotherDaySamplePlaying ? 'Pause Mother’s Day Sample' : 'Play Mother’s Day Sample'}
                >
                  {isMotherDaySamplePlaying ? (
                    <span className={styles.pauseIcon}></span>
                  ) : (
                    <span className={styles.playIcon}></span>
                  )}
                </button>
              </div>
            </div>
            <div className={styles.countdownColumn}>
              <div className={styles.countdownCard}>
                <img src="/images/mothers-day-gift.jpg" alt="Mother's Day special" className={styles.countdownImage} />
                <div className={styles.countdownTimer}>
                  <h3 className={styles.countdownTitle}>Mother's Day Countdown:</h3>
                  <div className={styles.timerDisplay}>
                    {['days', 'hours', 'minutes', 'seconds'].map((unit) => (
                      <div key={unit} className={styles.timerUnit}>
                        <div className={styles.timerNumber}>{formatNumber(timeRemaining[unit])}</div>
                        <div className={styles.timerLabel}>{unit.charAt(0).toUpperCase() + unit.slice(1)}</div>
                      </div>
                    ))}
                  </div>
                  <div className={styles.couponCodeContainer}>
                    <p className={styles.couponInstruction}>Use code at checkout:</p>
                    <div className={styles.couponCode}>MOM2025</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.musicWaveDivider}></div>
      </div>

      {showOrderNotification && (
        <div className={styles.orderNotification}>
          <span className={styles.checkIcon}>✓</span>
          <p style={{ color: '#7C3AED' }}>{notifications[currentNotificationIndex].text}</p>
        </div>
      )}

      <div className={styles.contentContainer}>
        <div className={styles.musicSamplesSection}>
          <h2 className={styles.sectionTitle}>Listen to Our Sample Songs</h2>
          <p className={styles.sectionSubtitle}>
            Experience the quality and emotion our songwriters can bring to your mother's special gift
          </p>
          <div className={styles.samplesGrid}>
            {musicSamples.map((sample) => (
              <div key={sample.id} className={styles.sampleCard}>
                <div className={styles.purpleTop}></div>
                <button
                  onClick={() => handlePlayPause(sample.id)}
                  className={`${styles.playButton} ${currentlyPlaying === sample.id ? styles.playing : ''}`}
                  aria-label={currentlyPlaying === sample.id ? `Pause ${sample.title}` : `Play ${sample.title}`}
                >
                  {currentlyPlaying === sample.id ? (
                    <span className={styles.pauseIcon}></span>
                  ) : (
                    <span className={styles.playIcon}></span>
                  )}
                </button>
                <h3 className={styles.sampleTitle}>{sample.title}</h3>
                <div className={styles.genreTag}>{sample.genre}</div>
                <p className={styles.sampleDescription}>{sample.description}</p>
              </div>
            ))}
          </div>
          <div className={styles.samplesFooter}>
            <p className={styles.samplesNote}>
              These are just examples. Your custom song will be tailored specifically to your mom's story!
            </p>
            <CTAButton className={styles.createSongButton}>Create Your Custom Song</CTAButton>
          </div>
          <div className={styles.musicWaveDivider}></div>
        </div>

        <div className={styles.testimonialsSection}>
          <h2 className={styles.sectionTitle}>What Our Customers Say</h2>
          <div className={styles.testimonialsGrid}>
            {[
              {
                text: "I surprised my mom with a custom song for her 60th birthday. She was in tears! It captured our family memories perfectly. Best gift I've ever given.",
                author: "Sarah T.",
              },
              {
                text: "My mom plays her song every day! The quality was beyond what I expected, and the lyrics captured everything I wanted to say but couldn't find the words for.",
                author: "Michael R.",
              },
              {
                text: "We played the song at our Mother's Day brunch and there wasn't a dry eye in the room. Worth every penny! Will be ordering again for other special occasions.",
                author: "Jennifer L.",
              },
            ].map((testimonial, index) => (
              <div key={index} className={styles.testimonialCard}>
                <div className={styles.testimonialRating}>
                  <div className={styles.ratingStars}>★★★★★</div>
                  <div className={styles.ratingNumber}>5/5</div>
                </div>
                <p className={styles.testimonialText}>"{testimonial.text}"</p>
                <div className={styles.testimonialAuthor}>{testimonial.author}</div>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.howItWorksSection}>
          <div className={styles.floatingNote1}></div>
          <div className={styles.floatingNote2}></div>
          <div className={styles.floatingNote3}></div>
          <div className={styles.floatingNote4}></div>
          <div className={styles.howItWorksContainer}>
            <h2 className={styles.sectionTitle}>How It Works</h2>
            <p className={styles.sectionSubtitle}>Three simple steps to her perfect song.</p>
            <div className={styles.stepsGrid}>
              {[
                { step: 1, title: "Tell Her Story", description: "Share what makes Mom special." },
                { step: 2, title: "We Create It", description: "MakeMeSongs turns your story into her song." },
                { step: 3, title: "Gift the Song", description: "Present a gift she’ll love." },
              ].map((step) => (
                <div key={step.step} className={styles.step}>
                  <div className={styles.stepNumber}>{step.step}</div>
                  <h3 className={styles.stepTitle}>{step.title}</h3>
                  <p className={styles.stepDescription}>{step.description}</p>
                </div>
              ))}
            </div>
            <CTAButton className={styles.startMySongButton}>Start Now</CTAButton>
          </div>
          <div className={styles.musicWaveDivider}></div>
        </div>

        <div className={styles.genreExplorerSection}>
          <h2 className={styles.sectionTitle}>Find Mom’s Perfect Style</h2>
          <p className={styles.sectionSubtitle}>Explore genres to match her vibe.</p>
          <div className={styles.genreCarouselContainer}>
            <button className={`${styles.carouselButton} ${styles.desktopLeftButton}`} onClick={prevGenreGroup}>‹</button>
            <div className={styles.genreCirclesWrapper}>
              <div
                ref={containerRef}
                className={styles.genreCirclesContainer}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onMouseDown={handleMouseDown}
              >
                {genreGroups.map((group, groupIndex) => (
                  <div key={groupIndex} className={`${styles.genreGroupSlide} ${group.length === 1 ? styles.singleGenreSlide : ''}`}>
                    <div className={styles.genreCardsGrid}>
                      {group.map((genre) => (
                        <div
                          key={genre.name}
                          className={`${styles.genreCard} ${playingGenre === genre.name ? styles.playing : ''}`}
                          style={{ '--genre-color': genre.color }}
                        >
                          <div className={styles.genreCardHeader}>
                            <span className={styles.genreIcon}>{genre.icon}</span>
                            <h3>{genre.name}</h3>
                          </div>
                          <p className={styles.genreDescription}>{genre.description}</p>
                          <button
                            className={`${styles.sampleButton} ${playingGenre === genre.name ? styles.playing : ''}`}
                            onClick={(e) => handleGenrePlay(genre, e)}
                          >
                            {playingGenre === genre.name ? (
                              <span className={styles.pauseIcon}></span>
                            ) : (
                              <span className={styles.playIcon}></span>
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <button className={`${styles.carouselButton} ${styles.desktopRightButton}`} onClick={nextGenreGroup}>›</button>
            <div className={styles.arrowHintContainer}>
              <div className={styles.leftArrowHint}>
                <span className={styles.arrowHintText}>Swipe to explore genres</span>
              </div>
              <div className={styles.rightArrowHint}>
                <span className={styles.arrowHintText}>More styles this way</span>
              </div>
            </div>
          </div>
          <div className={styles.carouselDots}>
            {genreGroups.map((_, index) => (
              <span
                key={index}
                className={`${styles.dot} ${currentGenreGroup === index ? styles.activeDot : ''}`}
                onClick={() => setCurrentGenreGroup(index)}
              ></span>
            ))}
          </div>
        </div>

        <div className={styles.countdownSeparator}>
          <div className={styles.countdownCard}>
            <div className={styles.countdownTimer}>
              <h3 className={styles.countdownTitle}>Mother's Day Countdown:</h3>
              <div className={styles.timerDisplay}>
                {['days', 'hours', 'minutes', 'seconds'].map((unit) => (
                  <div key={unit} className={styles.timerUnit}>
                    <div className={styles.timerNumber}>{formatNumber(timeRemaining[unit])}</div>
                    <div className={styles.timerLabel}>{unit.charAt(0).toUpperCase() + unit.slice(1)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.ctaSection}>
          <div className={styles.ctaContent}>
            <h2 className={styles.sectionTitle}>Make Mother’s Day Unforgettable</h2>
            <p className={styles.sectionSubtitle}>
              Use code <span className={styles.couponCode}>MOM2025</span> for 50% off a custom song!
            </p>
            <CTAButton className={styles.primaryButton}>Create Her Song</CTAButton>
          </div>
        </div>

        <footer className={styles.mothersDayFooter}>
          <div className={styles.footerContent}>
            <p className={styles.copyright}>© 2025 MakeMeSongs.com. All rights reserved.</p>
            <div className={styles.footerLinks}>
              <a href="/terms">Terms</a>
              <a href="/privacy">Privacy</a>
              <a href="/contact">Contact</a>
            </div>
          </div>
        </footer>

        {showStickyCta && (
          <div className={styles.stickyCta}>
            <div className={styles.stickyCtaContainer}>
              <p className={styles.stickyCtaText}>Save 50% on a Custom Song for Mom!</p>
              <div className={styles.stickyCouponCodeContainer}>
                <p className={styles.stickyCouponInstruction}>Use code:</p>
                <div className={styles.stickyCouponCode}>MOM2025</div>
              </div>
              <CTAButton className={styles.stickyCtaButton}>Get Started</CTAButton>
              <button
                onClick={() => setShowStickyCta(false)}
                className={styles.closeStickyCta}
                aria-label="Close sticky call-to-action"
              >
                ×
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MothersDay;